import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { companyActivityQueryOptions } from "./queries";

type Period = 7 | 14 | 30;

const PERIODS: { value: Period; label: string }[] = [
  { value: 7, label: "7 дней" },
  { value: 14, label: "14 дней" },
  { value: 30, label: "30 дней" },
];

// Match the in-app status palette: green for completed. The area fill
// is a vertical gradient — saturated near the line, fading to almost
// transparent at the axis — for the same airy look as the reference
// dashboard mock.
const COMPLETED_LINE = "#15803d";
const COMPLETED_FILL = {
  type: "linear" as const,
  x: 0,
  y: 0,
  x2: 0,
  y2: 1,
  colorStops: [
    { offset: 0, color: "rgba(21, 128, 61, 0.38)" },
    { offset: 1, color: "rgba(21, 128, 61, 0.02)" },
  ],
};

function formatTick(iso: string): string {
  // "2026-05-02" → "2 мая". Short and locale-aware without a JS date lib.
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

export function CompanyActivityChart() {
  const [period, setPeriod] = useState<Period>(14);
  const { data } = useQuery(companyActivityQueryOptions(period));

  const option: EChartsOption = useMemo(() => {
    const days = data?.days ?? [];
    const completed = data?.completed ?? [];
    return {
      grid: { left: 36, right: 16, top: 32, bottom: 28 },
      legend: {
        top: 0,
        right: 0,
        icon: "roundRect",
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { color: "#1f1f1f", fontSize: 12 },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line" },
        formatter: (raw) => {
          // ECharts hands a single object or an array depending on
          // trigger; both share `axisValue`/`marker`/`seriesName`/`value`.
          const arr = (Array.isArray(raw) ? raw : [raw]) as Array<{
            axisValue?: string | number;
            marker?: string;
            seriesName?: string;
            value?: number;
          }>;
          if (arr.length === 0) return "";
          const header = formatTick(String(arr[0]?.axisValue ?? ""));
          const body = arr
            .map((p) => {
              const marker = p.marker ?? "";
              const name = p.seriesName ?? "";
              const v = p.value ?? 0;
              return `${marker} ${name}: <b>${v}</b>`;
            })
            .join("<br/>");
          return `<div style="font-weight:600;margin-bottom:4px">${header}</div>${body}`;
        },
      },
      xAxis: {
        type: "category",
        data: days,
        boundaryGap: false,
        axisLabel: {
          color: "#7a7a7a",
          fontSize: 11,
          formatter: (v: string) => formatTick(v),
        },
        axisLine: { lineStyle: { color: "#e5e5e5" } },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        axisLabel: { color: "#7a7a7a", fontSize: 11 },
        splitLine: { lineStyle: { color: "#eeeeee" } },
      },
      series: [
        {
          name: "Завершено",
          type: "line",
          smooth: true,
          showSymbol: false,
          areaStyle: { color: COMPLETED_FILL },
          lineStyle: { color: COMPLETED_LINE, width: 1 },
          itemStyle: { color: COMPLETED_LINE },
          data: completed,
        },
      ],
    };
  }, [data]);

  const ready = (data?.days?.length ?? 0) > 0;

  return (
    <section className="company-activity">
      <div className="company-activity-head">
        <h2 className="company-section-title">Активность</h2>
        <div className="company-period" role="tablist" aria-label="Период">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              role="tab"
              aria-selected={period === p.value}
              className={`company-period-btn${period === p.value ? " company-period-btn--on" : ""}`}
              onClick={() => setPeriod(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="company-activity-canvas">
        {ready ? (
          <ReactECharts
            option={option}
            style={{ height: "100%", width: "100%" }}
            notMerge
            lazyUpdate
          />
        ) : (
          <div className="cmd-empty">Загрузка…</div>
        )}
      </div>
    </section>
  );
}
